import * as React from 'react'

// Installer downloads on the LC-AutoClient page (api_tokens/index), mounted through remount as
// <lcautoclient-downloads-component props-json='{"releases":[...]}'>. Releases come from LcAutoclientRelease through
// LcAutoclientReleasesHelper, newest first. The latest version's installers for the visitor's OS are offered first;
// on a Mac, Chromium-based browsers also say whether it's Apple silicon or Intel, others get both Mac installers.

const OS_ICONS = { mac: 'bi-apple', windows: 'bi-windows', linux: 'bi-ubuntu' }

// The installers aren't signed with a publisher certificate yet, so each OS warns the first time
const FIRST_LAUNCH = {
    mac: <>Open the <kbd>.dmg</kbd> and drag LC-AutoClient into Applications. The first time you open it, macOS blocks it because Apple hasn't notarized it yet: open <strong>System Settings → Privacy &amp; Security</strong> and click <strong>Open Anyway</strong> next to LC-AutoClient.</>,
    windows: <>Run the installer. If Windows says it protected your PC, click <strong>More info</strong>, then <strong>Run anyway</strong>.</>,
    linux: <>For the <kbd>.deb</kbd>, run <kbd>sudo apt install ./LC-AutoClient-*.deb</kbd>. For the AppImage, make it executable (<kbd>chmod +x LC-AutoClient-*.AppImage</kbd>) and run it.</>,
}

function detectOs() {
    const platform = (navigator.userAgentData?.platform || navigator.platform || navigator.userAgent || '').toLowerCase()
    if (platform.includes('win')) return 'windows'
    if (platform.includes('mac')) return 'mac'
    if (platform.includes('linux') && !/android/i.test(navigator.userAgent)) return 'linux'
    return null
}

// 'arm64' or 'x64' when the browser tells, null otherwise
function useMacArch(os) {
    const [arch, setArch] = React.useState(null)
    React.useEffect(() => {
        if (os !== 'mac' || !navigator.userAgentData?.getHighEntropyValues) return
        navigator.userAgentData.getHighEntropyValues(['architecture'])
            .then(({ architecture }) => setArch({ arm: 'arm64', x86: 'x64' }[architecture] ?? null))
            .catch(() => {})
    }, [os])
    return arch
}

const formatDate = (isoDate) => new Date(`${isoDate}T00:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
const formatSize = (bytes) => `${Math.round(bytes / 1e6)} MB`

function DownloadButton({ installer, primary }) {
    return (
        <a href={installer.url} className={`btn ${primary ? 'btn-primary' : 'btn-outline-secondary'}`} download data-turbo="false">
            <i className={`bi ${OS_ICONS[installer.os]} me-2`} />
            {installer.label}
            <span className="ms-2 small opacity-75">{formatSize(installer.byteSize)}</span>
        </a>
    )
}

export default function LcAutoclientDownloads({ releases }) {
    const os = React.useMemo(detectOs, [])
    const macArch = useMacArch(os)
    const [latest, ...older] = releases

    if (!latest) {
        return (
            <div className="kit-reg-card mb-3">
                <div className="kit-reg-section mb-0 border-0">
                    <h6 className="kit-reg-section-title"><i className="bi bi-download me-2" />Download LC-AutoClient</h6>
                    <p className="mb-0 text-muted">No LC-AutoClient installers have been published yet.</p>
                </div>
            </div>
        )
    }

    const forThisComputer = (installer) => installer.os === os && !(macArch && !installer.filename.includes(`-mac-${macArch}.`))
    const primary = latest.installers.filter(forThisComputer)
    const others = latest.installers.filter((installer) => !forThisComputer(installer))

    return (
        <div className="kit-reg-card mb-3">
            <div className="kit-reg-section mb-0 border-0">
                <h6 className="kit-reg-section-title"><i className="bi bi-download me-2" />Download LC-AutoClient {latest.version}</h6>
                <p className="small text-muted mb-2">Released {formatDate(latest.publishedOn)}</p>

                {primary.length > 0 && (
                    <div className="d-flex flex-wrap gap-2 mb-2">
                        {primary.map((installer) => <DownloadButton key={installer.filename} installer={installer} primary />)}
                    </div>
                )}
                {os === 'mac' && !macArch && (
                    <p className="small text-muted mb-2">
                        Not sure which Mac you have? In the Apple menu, choose <strong>About This Mac</strong>: a chip named Apple M1 or later is Apple silicon.
                    </p>
                )}
                {primary.length > 0 && <p className="small mb-3">{FIRST_LAUNCH[os]}</p>}

                {others.length > 0 && (
                    <>
                        <div className="small text-muted mb-1">{primary.length > 0 ? 'Other systems' : 'Choose your system'}</div>
                        <div className="d-flex flex-wrap gap-2 mb-2">
                            {others.map((installer) => <DownloadButton key={installer.filename} installer={installer} />)}
                        </div>
                    </>
                )}

                <details className="small mt-2">
                    <summary>SHA-256 checksums</summary>
                    <table className="table table-sm mt-2 mb-0">
                        <tbody>
                            {latest.installers.map((installer) => (
                                <tr key={installer.filename}>
                                    <td className="text-nowrap">{installer.filename}</td>
                                    <td className="font-monospace text-break">{installer.sha256 ?? 'not recorded'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </details>

                {older.length > 0 && (
                    <details className="small mt-2">
                        <summary>Older versions</summary>
                        <ul className="list-unstyled mt-2 mb-0">
                            {older.map((release) => (
                                <li key={release.version} className="mb-1">
                                    <strong>{release.version}</strong> <span className="text-muted">({formatDate(release.publishedOn)})</span>:{' '}
                                    {release.installers.map((installer, i) => (
                                        <React.Fragment key={installer.filename}>
                                            {i > 0 && ' · '}
                                            <a href={installer.url} download data-turbo="false">{installer.label}</a>
                                        </React.Fragment>
                                    ))}
                                </li>
                            ))}
                        </ul>
                    </details>
                )}
            </div>
        </div>
    )
}
